import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import fields from '../../lib/fields';
import { deriveRaceClassDefaults } from '../../lib/raceClassDefaults';

import FormFieldsCompact from '../../components/form/fieldsCompact';

const EDIT_FIELDS = [
    'year', 'title', 'tag', 'currency',
    'startDate', 'endDate', 'signupStartDate', 'signupEndDate', 'signupDelayedEndDate',
    'signupfee', 'signuppayment', 'signuppaymentDelayed', 'musherMeetingStartDate', 'musherMeetingEndDate',
    'checkInStartDate', 'checkInEndDate',
    'dogs', 'dogsInfo', 'distance', 'requiredRestMinutes', 'elevation', 'raceClassTrack', 'obligRest',
    'maxMushers',
    'ingress', 'body', 'info', 'terms', 'trackInfo', 'prices', 'qualifying', 'rules', 'readyToSignup',
    'images', 'files', 'sponsors',
    'infoEn', 'termsEn', 'trackInfoEn', 'pricesEn', 'qualifyingEn', 'rulesEn',
    'checkpoints', 'waypoints',
    'bibColor', 'startBib', 'endBib', 'startInterval',
    'sortOrderResults', 'doNotCountRestTime',
];

@observer
class RaceClassEdit extends Component {
    constructor(props) {
        super(props);
        this.state = {
            sections: fields.get('raceClass'),
            removeConfirm: false,
            errorMessage: null,
        };
    }

    loadRaceClass = async () => {
        const { id } = this.props;
        const { raceClassStore } = this.props.stores;
        raceClassStore.updateKeyValue('raceClass', {});
        await raceClassStore.load(id, false, { addData: ['files'] });
    }

    prefillNewRaceClass = () => {
        const { raceId, race = {} } = this.props;
        const { raceClassStore } = this.props.stores;
        const defaults = deriveRaceClassDefaults(race.id > 0 ? race : { ...race, id: raceId });
        raceClassStore.updateKeyValue('newRaceClass', {});
        raceClassStore.updateObjectKeyValue('newRaceClass', 'race', defaults.race);
        raceClassStore.updateObjectKeyValue('newRaceClass', 'year', defaults.year);
    }

    createRaceClass = async () => {
        const { callback = () => {} } = this.props;
        const { raceClassStore } = this.props.stores;
        const { newRaceClass } = raceClassStore;
        if (!newRaceClass.title) {
            this.setState({ errorMessage: <Text id='raceclass.title-required'>Title is required.</Text> });
            return;
        }
        const response = await raceClassStore.insert(newRaceClass);
        if (response.status === 201) {
            raceClassStore.updateKeyValue('newRaceClass', {});
            callback();
        } else {
            this.setState({ errorMessage: response.message || 'Could not create raceclass.' });
        }
    }

    removeRaceClass = async () => {
        const { removeConfirm } = this.state;
        if (!removeConfirm) {
            this.setState({ removeConfirm: true });
            return;
        }
        const { id, callback = () => {} } = this.props;
        const { raceClassStore } = this.props.stores;
        const result = await raceClassStore.deleteRaceClass(id);
        if (result.ok) {
            callback();
        } else {
            this.setState({
                removeConfirm: false,
                errorMessage: result.signups > 0
                    ? <Text id='raceclass.delete-blocked' fields={{ count: result.signups }}>
                        This raceclass still has {result.signups} signups and can not be deleted.
                        Move or remove the signups first.
                    </Text>
                    : <Text id='raceclass.delete-error'>Could not delete the raceclass. Please try again.</Text>,
            });
        }
    }

    cancelRemove = () => {
        this.setState({ removeConfirm: false });
    }

    done = () => {
        const { callback = () => {} } = this.props;
        callback();
    }

    componentDidMount() {
        const { isNew } = this.props;
        if (isNew) {
            this.prefillNewRaceClass();
        } else {
            this.loadRaceClass();
        }
    }

    render() {
        const { isNew, drawerLevel } = this.props;
        const { sections, removeConfirm, errorMessage } = this.state;
        const { raceClassStore } = this.props.stores;

        return (<>
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 120px;'>
                    <h5 class='ms-3 mb-3'>
                        {isNew ? <Text id='raceclass.new'>New raceclass</Text> : <Text id='raceclass.edit'>Edit raceclass</Text>}
                    </h5>
                    {!isNew && <div class='ms-3 mb-2'>
                        <small class='text-body-secondary'>
                            <Text id='raceclass.auto-save'>Changes are saved automatically.</Text>
                        </small>
                    </div>}
                    <div class='bg-light d-flex flex-column justify-content-start h-100 pt-3'>
                        <FormFieldsCompact
                            stores={this.props.stores}
                            sections={sections}
                            labelWidth={200}
                            defaultShowOnlyRecommended={true}
                            fields={EDIT_FIELDS}
                            holdingStore={raceClassStore}
                            objectName={isNew ? 'newRaceClass' : 'raceClass'}
                            isNew={isNew}
                            drawerLevel={drawerLevel}
                            isAdmin={false}
                        />
                    </div>
                </div>
            </div>

            <div class='w-100 d-flex flex-column justify-content-start mt-3 px-4 py-3 bg-light fixed-bottom' style='bottom: 0; left: 0;'>
                {errorMessage && <div class='alert alert-danger py-2 mb-2'>{errorMessage}</div>}
                {isNew ? (
                    <button type='button' class='btn btn-primary rounded-pill' onClick={this.createRaceClass}>
                        <Text id='raceclass.create'>Create raceclass</Text>
                    </button>
                ) : (
                    <div class='d-flex flex-row justify-content-between'>
                        {removeConfirm ? (
                            <div class='d-flex flex-row align-items-center'>
                                <button type='button' class='btn btn-danger rounded-pill me-2' onClick={this.removeRaceClass}>
                                    <Text id='raceclass.remove-confirm'>Are you sure? Click to remove!</Text>
                                </button>
                                <button type='button' class='btn btn-outline-secondary rounded-pill' onClick={this.cancelRemove}>
                                    <Text id='raceclass.remove-cancel'>Cancel</Text>
                                </button>
                            </div>
                        ) : (
                            <button type='button' class='btn btn-outline-danger rounded-pill' onClick={this.removeRaceClass}>
                                <i class='fa-solid fa-trash' /> <Text id='raceclass.remove'>Remove raceclass</Text>
                            </button>
                        )}
                        <button type='button' class='btn btn-primary rounded-pill' onClick={this.done}>
                            <Text id='raceclass.done'>Done</Text>
                        </button>
                    </div>
                )}
            </div>
        </>);
    }
}

export default RaceClassEdit;
